import { motion } from "framer-motion";
import { BadgeCheck, Award, ShieldHalf, Timer } from "lucide-react";
import { useLang } from "@/lib/i18n";

const ICONS = [Award, BadgeCheck, ShieldHalf, Timer];

export default function Certs() {
  const { t } = useLang();
  const CERTS = t("certs.items").map((c, i) => ({ ...c, icon: ICONS[i] }));
  return (
    <section id="credentials" data-testid="credentials-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6">
        <p className="font-mono text-xs tracking-[0.25em] uppercase text-primary mb-4">{t("certs.overline")}</p>
        <h2 className="font-heading font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mb-14">
          {t("certs.h1")}<span className="text-accent">.</span>
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {CERTS.map((c, i) => (
            <motion.div
              key={c.title}
              data-testid={`cert-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="border border-black bg-white p-6 sm:p-8 text-center hover:border-accent transition-colors duration-300"
            >
              <c.icon className="w-8 h-8 mx-auto text-primary mb-4" strokeWidth={1.5} />
              <p className="font-heading font-bold text-lg tracking-tight">{c.title}</p>
              <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground mt-1">{c.sub}</p>
            </motion.div>
          ))}
        </div>

        <p className="font-mono text-xs tracking-[0.25em] uppercase text-muted-foreground mt-16 mb-6">{t("certs.gallery")}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl" data-testid="cert-gallery">
          {[
            {
              src: "/cert-md102.png",
              alt: "Microsoft Certified Associate badge — Microsoft 365 Certified: Endpoint Administrator Associate",
              title: t("certs.g1title"),
              meta: t("certs.g1meta"),
              href: "https://learn.microsoft.com/en-us/users/hamdourw93/credentials/13d3a44f114b1212",
              testid: "gallery-md102",
            },
            {
              src: "/cert-securityplus.png",
              alt: "CompTIA Security+ certification badge issued to Hamdou Rabi Wane",
              title: t("certs.g2title"),
              meta: t("certs.g2meta"),
              href: "https://www.credly.com/badges/15b2cd36-ec22-4e76-b7ee-8e0bcaf69a99",
              testid: "gallery-securityplus",
            },
          ].map((g, i) => (
            <motion.a
              key={g.title}
              href={g.href}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={g.testid}
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              className="block border border-black bg-white p-3 hover:border-accent transition-colors duration-300"
            >
              <div className="overflow-hidden border border-black bg-white">
                <img
                  src={g.src}
                  alt={g.alt}
                  loading="lazy"
                  className="w-full h-72 object-contain"
                />
              </div>
              <div className="px-2 py-4">
                <p className="font-heading font-bold tracking-tight text-sm">{g.title}</p>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-muted-foreground mt-1.5">{g.meta}</p>
                <p className="font-mono text-[10px] tracking-[0.15em] uppercase text-primary mt-2">{t("certs.verify")}</p>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </section>
  );
}
